import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { insertTimestampIntoText } from "../../../helpers/getTimestamp";
import { updateClientServiceStatus } from "../../../services/clientService/updateClientServiceStatus";
import { getFirstJobName } from "../../../services/clientServiceSubscriptionTask/getFirstJobName";
import ButtonAddTimestamp from "../../../utils/ButtonAddTimestamp";
import AssignedByListBox from "./AssignedByListBox";
import ButtonModal from "./ButtonModal";
import InputDateModal from "./InputDateModal";
import InputTextAreaModal from "./InputTextAreaModal";
import { getSubscriptionTeamMembers } from "../../../services/clientServiceSubscription/getSubscriptionTeamMembers";
import { usePermissions } from "../../../context/PermissionsContext";

const getLocalISODate = () => {
    // Returns YYYY-MM-DD in local time (not UTC)
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const AssignEmployeToTaskWhenServiceStarts = ({
    open,
    setOpen,
    loggedInEmployee,
    clientSubscriptionId,
    clientSubscription = null,
    newClientService,
    clientId,
    showStartRequirementNote = false,
    required = false,
    staged = false,
    onPrepared,
    onStarted,
}) => {
    const { can, permissionsLoading } = usePermissions();
    // Starting the first Step is a derived effect of Start Service, so this
    // flow intentionally uses the Service write permission.
    const canWriteServices = can("client_services.write");
    const [isSaving, setIsSaving] = useState(false);
    const [displayNotes, setDisplayNotes] = useState(false);

    const [firstJob, setFirstJob] = useState({
        assignedEmployees: [],
        notes: "",
        dueDate: getLocalISODate(),
    });

    const notesRef = useRef(null);
    const completeButtonRef = useRef(null);

    const [firstJobName, setFirstJobName] = useState("");
    const [teamMembers, setTeamMembers] = useState([]); // ✅ Always array
    const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
    const [teamMembersError, setTeamMembersError] = useState("");

    const toggleNotes = () => setDisplayNotes((v) => !v);

    const validateAssignedEmployee = (arr) =>
        Array.isArray(arr) && arr.length > 0 ? arr[0] : null;

    const hasAvailableEmployees = useMemo(
        () => Array.isArray(teamMembers) && teamMembers.length > 0,
        [teamMembers],
    );

    // Reset form every time modal opens for a different subscription
    useEffect(() => {
        if (!open) return;

        setDisplayNotes(false);
        setTeamMembersError("");
        setFirstJob({
            assignedEmployees: [],
            notes: "",
            dueDate: getLocalISODate(),
        });
        // eslint-disable-next-line
    }, [open, clientSubscriptionId, clientSubscription]);

    // Load the name of the first job
    useEffect(() => {
        if (permissionsLoading || !canWriteServices || !clientSubscriptionId) return;

        const run = async () => {
            try {
                const response = await getFirstJobName(clientSubscriptionId);
                setFirstJobName(response.data || "");
            } catch (e) {
                setFirstJobName("");
            }
        };

        run();
    }, [clientSubscriptionId, canWriteServices, permissionsLoading]);

    // Load subscription team members (ONLY when modal is open)
    useEffect(() => {
        if (permissionsLoading || !canWriteServices || !open) return;
        if (!clientSubscriptionId) return;

        const getTeamMembers = async () => {
            setIsLoadingTeamMembers(true);
            setTeamMembersError("");

            try {
                const response =
                    await getSubscriptionTeamMembers(clientSubscriptionId);

                const members =
                    response?.data?.teamMembers &&
                    Array.isArray(response.data.teamMembers)
                        ? response.data.teamMembers
                        : [];

                setTeamMembers(members);

                if (members.length === 0) {
                    setTeamMembersError(
                        "No team members found for this service. Please assign a Team/Department to this service first.",
                    );
                }
            } catch (err) {
                console.error("Error loading team members:", err);
                setTeamMembers([]);
                setTeamMembersError(
                    "Couldn't load team members. Please try again or check the service setup.",
                );
            } finally {
                setIsLoadingTeamMembers(false);
            }
        };

        getTeamMembers();
    }, [clientSubscriptionId, open, canWriteServices, permissionsLoading]);

    // Field change handlers
    const onHandleChangeAssignedEmployees = (emp) => {
        setFirstJob((prev) => ({ ...prev, assignedEmployees: emp ? [emp] : [] }));
    };

    const onHandleChange = (e) => {
        const { name, value } = e.target;
        setFirstJob((prev) => ({ ...prev, [name]: value }));
    };

    const onHandleDueDateChange = (e) => {
        const date = e.target.value;

        // Keep your original rule
        if (firstJob.assignedEmployees.length > 0 && firstJob.dueDate > date) {
            toast.error("The due date cannot be before the assigned date.");
            return;
        }

        setFirstJob((prev) => ({ ...prev, dueDate: date }));
    };

    // Timestamp insert
    const onHandleClickAddTimestamp = (fieldName) => {
        const textarea = notesRef.current;
        if (!textarea) return;

        const { selectionStart, selectionEnd } = textarea;
        const current = firstJob[fieldName] || "";
        const { caretPosition, value: updated } = insertTimestampIntoText(
            current,
            selectionStart,
            selectionEnd,
            loggedInEmployee,
        );

        setFirstJob((prev) => ({
            ...prev,
            [fieldName]: updated,
        }));

        textarea.focus();
        textarea.setSelectionRange(caretPosition, caretPosition);
    };

    const validateForm = () => {
        if (!hasAvailableEmployees) {
            toast.error(
                "This service has no available employees. Assign a Team/Department first.",
            );
            return false;
        }

        if (firstJob.assignedEmployees.length < 1) {
            toast.error("Select at least one employee please");
            return false;
        }

        if (!firstJob.dueDate) {
            toast.error("Choose a due date please");
            return false;
        }

        return true;
    };

    const onHandleSubmit = (e) => {
        e.preventDefault();
        if (permissionsLoading || !canWriteServices || isSaving) return;
        if (!validateForm()) return;
        onHandleUpdateServiceStatus();
    };

    const onHandleUpdateServiceStatus = async () => {
        if (permissionsLoading || !canWriteServices || isSaving) return;

        setIsSaving(true);
        try {
            const payloadJob = {
                ...firstJob,
                status: 1,
                assignedByEmployee: loggedInEmployee,
            };

            if (staged) {
                await onPrepared?.(payloadJob);
                return;
            }

            const response = await updateClientServiceStatus(
                clientSubscriptionId,
                1,
                payloadJob,
                loggedInEmployee,
            );

            if (!response?.success && response?.status !== 403) {
                toast.error(response?.data?.message || "Failed to start service.");
                return;
            }
            if (!response?.success) return;

            toast.success(response.data.message);
            setOpen(false);
            await onStarted?.(response);
            await newClientService?.();
        } catch (err) {
            console.error("Error starting service:", err);
            toast.error("Error starting service.");
        } finally {
            setIsSaving(false);
        }
    };

    const onHandleCancel = () => {
        if (!required) setOpen(false);
    };

    const saveDisabled =
        isLoadingTeamMembers ||
        permissionsLoading ||
        !canWriteServices ||
        isSaving ||
        !hasAvailableEmployees ||
        firstJob.assignedEmployees.length < 1;

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={onHandleCancel}
                    className={`relative ${required ? "z-[240]" : "z-50"}`}
                >
                    <div className="fixed inset-0 bg-black/30" />
                    <div className="fixed inset-0 w-screen overflow-y-auto">
                        <div className="flex items-center justify-center min-h-full p-4">
                            <Dialog.Panel className="relative w-full px-4 pt-5 pb-4 bg-white rounded-lg shadow-xl sm:my-8 sm:max-w-lg sm:p-6">
                                <Dialog.Title className="w-full text-lg font-medium text-center text-gray-900">
                                    Assign step {firstJobName || "service"}
                                </Dialog.Title>

                                <form
                                    onSubmit={onHandleSubmit}
                                    className="mt-4 space-y-4"
                                >
                                    {/* ✅ Always render something: loading / selector / error */}
                                    {isLoadingTeamMembers ? (
                                        <div className="p-3 text-sm rounded bg-slate-50 text-slate-600">
                                            Loading team members...
                                        </div>
                                    ) : (
                                        <>
                                            <AssignedByListBox
                                                label="Employee"
                                                employees={teamMembers} // ✅ Always array
                                                assignedBy={validateAssignedEmployee(
                                                    firstJob.assignedEmployees,
                                                )}
                                                onChange={
                                                    onHandleChangeAssignedEmployees
                                                }
                                            />

                                            {teamMembersError ? (
                                                <div className="p-3 text-sm text-red-700 rounded bg-red-50">
                                                    {teamMembersError}
                                                </div>
                                            ) : null}
                                        </>
                                    )}

                                    {firstJob.assignedEmployees.length > 0 && (
                                        <InputDateModal
                                            label="Due Date"
                                            name="dueDate"
                                            onChange={onHandleDueDateChange}
                                            value={firstJob.dueDate}
                                        />
                                    )}

                                    {!displayNotes ? (
                                        <button
                                            type="button"
                                            onClick={toggleNotes}
                                            className="inline-flex items-center p-1 mt-4 text-white bg-teal-500 rounded-sm hover:bg-teal-600"
                                        >
                                            Add notes
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={toggleNotes}
                                            className="inline-flex items-center p-1 mt-4 text-white bg-red-500 rounded-sm hover:bg-red-600"
                                        >
                                            Hide notes
                                        </button>
                                    )}

                                    {displayNotes && (
                                        <>
                                            <InputTextAreaModal
                                                ref={notesRef}
                                                label="Notes"
                                                name="notes"
                                                onChange={onHandleChange}
                                                value={firstJob.notes}
                                            />
                                            <ButtonAddTimestamp
                                                onHandleClick={() =>
                                                    onHandleClickAddTimestamp(
                                                        "notes",
                                                    )
                                                }
                                            />
                                        </>
                                    )}

                                    {showStartRequirementNote && (
                                        <p className="text-sm font-medium text-slate-600">
                                            You must assign the first step in order
                                            to start the service
                                        </p>
                                    )}

                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <ButtonModal
                                            name={staged ? "Continue" : "Save"}
                                            type="submit"
                                            ref={completeButtonRef}
                                            disabled={saveDisabled}
                                            className={`inline-flex justify-center w-full px-4 py-2 text-white rounded-md sm:col-start-2
                                                ${
                                                    saveDisabled
                                                        ? "bg-slate-300 cursor-not-allowed"
                                                        : "bg-teal-500 hover:bg-teal-600"
                                                }`}
                                        />
                                        {!required ? (
                                            <ButtonModal
                                                name="Cancel"
                                                type="button"
                                                onClick={onHandleCancel}
                                                className="inline-flex justify-center w-full px-4 py-2 mt-3 text-gray-700 bg-white rounded-md hover:bg-gray-100 sm:mt-0 sm:col-start-1"
                                            />
                                        ) : null}
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default AssignEmployeToTaskWhenServiceStarts;
